<template>
  <div class="custom-region-map">
    <app-map
      v-bind="$attrs"
      v-on="$listeners"
      :zoom="mapZoom"
      :center="mapCenter"
      :value="value"
      :is-editable="!geocoderValue && !boundaryFileValue"
      :bounds="bounds"
      :is-rectangle-drawing-mode="isRectangleDrawingMode"
      :is-loading="$attrs.isLoading || isGeometryLoading"
      is-clearable
      ref="map"
      @cleared="clearMap"
    >
      <template v-slot:prepend>
        <l-control position="topleft" class="custom-region-map__geocoder-control">
          <region-search-autocomplete
            class="custom-region-map__search-autocomplete"
            attach="#map-region-search-autocomplete"
            wrapper-id="map-region-search-autocomplete"
            ref="mapRegionSearchAutocomplete"
            v-model="searchResult"
            @search="sendEventToCounters"
            prepend-inner-icon="mdi-magnify"
            append-icon=""
            filled
            solo
            :outlined="false"
            flat
            clearable
            :placeholder="$t('app.searchLocation')"
          ></region-search-autocomplete>
        </l-control>
        <l-control position="topleft" class="custom-region-map__geocoder-control">
          <boundary-from-file @geojsonLoaded="processLoadedBoundaryFile" :loaded="!!boundaryFileValue" />
        </l-control>

        <l-control position="topleft">
          <v-btn-toggle
            class="drawing-mode-toggle"
            borderless
            mandatory
            color="white"
            background-color="white"
            v-model="toggleDrawingModeValue"
            @change="onDrawingModeButtonToggle"
          >
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!!boundaryFileValue || !!searchResult"
                  color="white"
                  class="drawing-mode-button drawing-mode-button--polygon"
                  :aria-label="$t('product.custom.drawPolygon')"
                >
                  <v-icon :class="isRectangleDrawingMode ? 'mode-off' : 'mode-active'">mdi-vector-polygon </v-icon>
                </v-btn>
              </template>
              {{ $t('product.custom.drawPolygon') }}
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!!boundaryFileValue || !!searchResult"
                  color="white"
                  class="drawing-mode-button drawing-mode-button--rectangle"
                  :aria-label="$t('product.custom.drawRectangle')"
                >
                  <v-icon :class="isRectangleDrawingMode ? 'mode-active' : 'mode-off'">mdi-vector-square </v-icon>
                </v-btn>
              </template>
              {{ $t('product.custom.drawRectangle') }}
            </v-tooltip>
          </v-btn-toggle>
        </l-control>
        <slot name="prepend"></slot>
      </template>
      <template v-slot:append>
        <slot name="append"></slot>
      </template>
    </app-map>
    <div class="custom-region-map__hint" v-if="!value">
      {{ $t('product.custom.mapHint') }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import L from 'leaflet';
import { mapState, mapMutations } from 'vuex';
import { LControl } from 'vue2-leaflet';
import mapService from '@/services/mapService';
import AppMap from '@/components/map/AppMap.vue';
import BoundaryFromFile from '@/components/map/BoundaryFromFileControl/BoundaryFromFile.vue';
import RegionSearchAutocomplete from '@/components/app/RegionSearchAutocomplete.vue';
import regionService from '@/services/regionService';
import * as nominatimRegionService from '@/services/nominatimRegionService';
import {
  convertNominatimLookupItemToSearchResult,
  convertRegionDataToSearchResult,
  areRegionsEqual,
} from '@/utils/regionSearchUtils';
import {
  clearRegionQueriesFromRoute,
  setNominatimOsmIdToRouteQuery,
  setRegionCodeToRouteQuery,
} from '@/utils/regionRouterUtils';

export default {
  name: 'CustomRegionMap',
  components: {
    AppMap,
    LControl,
    BoundaryFromFile,
    RegionSearchAutocomplete,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      mapCenter: [0, 0],
      mapZoom: 2,
      toggleDrawingModeValue: undefined,
      searchResult: null,
      regionDataAbortController: undefined,
      regionGeometryAbortController: undefined,
      isGeometryLoading: false,
    };
  },
  computed: {
    ...mapState('map', ['geocoderValue', 'boundaryFileValue', 'isRectangleDrawingMode']),
    mapObject() {
      return this.$refs.map.mapObject;
    },
    bounds() {
      const { bounds } = this.$route.query;
      return !this.value ? bounds && JSON.parse(bounds) : undefined;
    },
    regionInRoute() {
      return this.$route.query && (this.$route.query.osmId || this.$route.query.regionCode);
    },
    regionFromSearch() {
      return this.regionInRoute || this.geocoderValue;
    },
  },
  watch: {
    regionFromSearch: {
      async handler() {
        this.cancelAllRequests();
        this.regionDataAbortController = new AbortController();

        const hasCorrectGeocoderValue =
          this.geocoderValue &&
          (areRegionsEqual(this.geocoderValue.meta, {
            source: 'nominatim',
            regionData: { osmId: this.$route.query.osmId },
          }) ||
            areRegionsEqual(this.geocoderValue.meta, {
              source: 'catalog',
              regionData: { code: this.$route.query.regionCode },
            }));
        if (!this.regionFromSearch) this.searchResult = null;

        try {
          if (hasCorrectGeocoderValue) {
            this.searchResult = this.geocoderValue.meta;
          } else if (this.$route.query.osmId) {
            const nominatimRegionData = await nominatimRegionService.getNominatimRegionData({
              osmId: this.$route.query.osmId,
              options: { signal: this.regionDataAbortController.signal },
            });
            this.searchResult = nominatimRegionData && convertNominatimLookupItemToSearchResult(nominatimRegionData);
          } else if (this.$route.query.regionCode) {
            const regionData = await regionService.getRegionData(this.$route.query.regionCode, {
              signal: this.regionDataAbortController.signal,
            });
            this.searchResult = regionData && convertRegionDataToSearchResult(regionData);
          }
        } catch (e) {
          if (!axios.isCancel(e)) throw e;
        }
      },
      immediate: true,
    },
    async searchResult(searchResultValue) {
      this.SET_BOUNDARY_FILE_VALUE(null);
      this.cancelAllRequests();
      this.regionGeometryAbortController = new AbortController();

      if (searchResultValue) {
        const doesValueMatchSearchValue =
          !!this.value && !!this.value.meta && areRegionsEqual(this.value.meta, searchResultValue);

        try {
          if (doesValueMatchSearchValue) {
            const { geometry } = this.value;
            this.setSearchResultGeometryAsValue({ meta: searchResultValue, geom: geometry });
          } else if (searchResultValue.source === 'nominatim') {
            this.isGeometryLoading = true;
            const geometry = await nominatimRegionService.getNominatimRegionGeometry({
              osmId: searchResultValue.regionData.osmId,
              options: {
                signal: this.regionGeometryAbortController.signal,
              },
            });
            clearRegionQueriesFromRoute(this.$route, this.$router);
            this.setSearchResultGeometryAsValue({ meta: searchResultValue, geom: geometry });
            setNominatimOsmIdToRouteQuery(this.$route, searchResultValue.regionData.osmId, this.$router);
            this.isGeometryLoading = false;
          } else if (searchResultValue.source === 'catalog') {
            this.isGeometryLoading = true;
            const geometry = await regionService.getRegionGeometry(searchResultValue.regionData.code, {
              signal: this.regionGeometryAbortController.signal,
            });
            clearRegionQueriesFromRoute(this.$route, this.$router);
            this.setSearchResultGeometryAsValue({ meta: searchResultValue, geom: geometry });
            setRegionCodeToRouteQuery(this.$route, searchResultValue.regionData.code, this.$router);
            this.isGeometryLoading = false;
          }
        } catch (e) {
          if (!axios.isCancel(e)) throw e;
        }
      } else {
        clearRegionQueriesFromRoute(this.$route, this.$router);
        this.setSearchResultGeometryAsValue(null);
      }
    },
  },
  async mounted() {
    this.$nextTick(() => {
      L.DomEvent.disableScrollPropagation(this.$refs.mapRegionSearchAutocomplete.$el);
    });

    this.toggleDrawingModeValue = this.isRectangleDrawingMode ? 1 : 0;
    if (this.value && this.value.geometry) {
      mapService.fitBoundsToGeojson(this.mapObject, this.value.geometry);
    } else if ('geolocation' in navigator && !this.$route.query.bounds && !this.regionFromSearch) {
      navigator.geolocation.getCurrentPosition((position) => {
        // repeat !this.value condition, because value may be settled asynchronously
        if (!this.value) {
          this.mapCenter = [position.coords.latitude, position.coords.longitude];
          setTimeout(() => {
            // parrallel zoom and center updating doesn't work
            this.mapZoom = 9;
          }, 300);
        }
      });
    }
  },
  beforeDestroy() {
    this.cancelAllRequests();
  },
  methods: {
    ...mapMutations({
      SET_GEOCODER_VALUE: 'map/SET_GEOCODER_VALUE',
      SET_BOUNDARY_FILE_VALUE: 'map/SET_BOUNDARY_FILE_VALUE',
      SET_IS_RECTANGLE_DRAWING_MODE: 'map/SET_IS_RECTANGLE_DRAWING_MODE',
    }),
    redraw() {
      if (this.$refs.map) this.$refs.map.redraw();
    },
    processLoadedBoundaryFile(boundaryFileValue) {
      this.clearMap();
      this.$nextTick(() => {
        if (window.ym) window.ym(42050389, 'reachGoal', 'upload-boundary');
        this.$emit('input', {
          geometry: boundaryFileValue?.geometry,
          meta: { source: 'file', regionData: { filename: boundaryFileValue?.filename } },
        });
        this.SET_BOUNDARY_FILE_VALUE(boundaryFileValue.geometry);
        mapService.fitBoundsToGeojson(this.mapObject, boundaryFileValue.geometry);
      });
    },
    clearMap() {
      if (this.searchResult) this.searchResult = null;
      this.SET_GEOCODER_VALUE(null);
      clearRegionQueriesFromRoute(this.$route, this.$router);
      this.SET_BOUNDARY_FILE_VALUE(null);
    },
    onDrawingModeButtonToggle(val) {
      // toogle button at first fires undefined value instead of 0
      this.toggleDrawingModeValue = val || 0;
      if (val === 0 || !val) {
        this.SET_IS_RECTANGLE_DRAWING_MODE(false);
      } else if (val === 1) {
        this.SET_IS_RECTANGLE_DRAWING_MODE(true);
      }
    },
    setSearchResultGeometryAsValue(value) {
      if (!!value && value.geom) {
        this.$emit('input', { geometry: value.geom, meta: value.meta });
        if (value.geom) {
          mapService.fitBoundsToGeojson(this.mapObject, value.geom);
        }
      } else if (this.value) {
        this.$refs.map.stopDrawing();
        this.$emit('input', null);
      }
      this.SET_GEOCODER_VALUE(value);
    },
    cancelAllRequests() {
      if (this.regionDataAbortController) {
        this.regionDataAbortController.abort();
        this.regionDataAbortController = undefined;
      }
      if (this.regionGeometryAbortController) {
        this.regionGeometryAbortController.abort();
        this.regionGeometryAbortController = undefined;
      }
    },
    sendEventToCounters(query) {
      if (window.ym) window.ym(42050389, 'reachGoal', 'custom-map-search', { query });
    },
  },
};
</script>

<style lang="scss" scoped>
.mode-active {
  color: $success !important;
}

.mode-off {
  color: $primary !important;
}

.mode-off:hover {
  color: $success !important;
}

.drawing-mode-toggle {
  box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.07),
    0px 1px 5px 0px rgba(0, 0, 0, 0.06);
}

.custom-region-map {
  position: relative;

  @media (min-width: map-get($grid-breakpoints, 'md')) {
    height: 100%;
  }

  &__hint {
    position: absolute;
    z-index: 5;
    bottom: 0px;
    left: 0;
    right: 0;
    background-color: $ng-secondary;
    color: $primary;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    word-spacing: 1px;
    padding: 16px 20px;
    font-size: 14px;
    text-align: center;
    font-family: $heading-font-family;
    font-weight: bold;
    border-top: 1px solid #d3e3f2;
  }

  &__geocoder::v-deep .v-input {
    font-size: map-get($font-size, 'small');
    box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.07),
      0px 1px 5px 0px rgba(0, 0, 0, 0.06) !important;
  }

  &::v-deep {
    .leaflet-top.leaflet-left {
      display: flex;
      flex-flow: row wrap;
      align-items: center;
    }

    .leaflet-top.leaflet-left .leaflet-control {
      float: none;
    }
  }

  &__geocoder-control {
    z-index: 1000;
  }

  &__search-autocomplete {
    z-index: 1000;
    width: 260px;
  }
}
</style>
